import AccountManager from './accountManager.js';
import d from './DOM.js';

export default class Analytics {
	static init() {
		Analytics.loadData();
	}

	static async loadData() {
		if (!AccountManager.currentAccount) {
			setTimeout(() => Analytics.loadData(), 500); // Retry once account manager loads
			return;
		}

		try {
			const query = `?accountNumber=${AccountManager.currentAccount.accountNumber}`;
			const res = await fetch(`/api/dashboard/metrics${query}`);
			const data = await res.json();

			if (data.error) {
				console.error(data.error);
				return;
			}

			Analytics.renderHeatmap(data.timeBased.hourOfDay);
			Analytics.renderVolumeChart(data.timeBased.hourOfDay);
			Analytics.renderInsights(data.timeBased.hourOfDay);
			Analytics.renderSessionGuide();
		} catch (err) {
			console.error('Failed to load analytics data', err);
		}
	}

	static renderSessionGuide() {
		const container = d.qs('#session-guide-container');
		if (!container) return;

		const sessions = [
			{ name: 'Sydney', utcStart: 22, utcEnd: 7 },
			{ name: 'Tokyo', utcStart: 0, utcEnd: 9 },
			{ name: 'London', utcStart: 8, utcEnd: 17 },
			{ name: 'New York', utcStart: 13, utcEnd: 22 }
		];

		const now = new Date();
		const utcHour = now.getUTCHours();
		const localOffset = now.getTimezoneOffset() / -60;
		
		// Broker Time: Most brokers use NY Close (5pm ET) as 00:00.
		// That is usually UTC+2 or UTC+3 depending on DST.
		// A standard way to calculate "Broker Time" (GMT+2/3) is to take UTC and add offset.
		// Let's assume standard Broker Time is UTC+2 (Standard) or UTC+3 (DST).
		// Many traders just want to see it relative to the "NY Close" day start.
		const brokerOffset = 2; // Default GMT+2

		const html = `
			<table class="session-table">
				<thead>
					<tr>
						<th>Session</th>
						<th>Local Time</th>
						<th>UTC Time</th>
						<th>Broker Time</th>
						<th>Status</th>
					</tr>
				</thead>
				<tbody>
					${sessions.map(s => {
						const isDayOver = s.utcEnd < s.utcStart;
						const isActive = isDayOver 
							? (utcHour >= s.utcStart || utcHour < s.utcEnd)
							: (utcHour >= s.utcStart && utcHour < s.utcEnd);

						const formatTime = (h: number, offset: number) => {
							let hour = (h + offset) % 24;
							if (hour < 0) hour += 24;
							return `${hour.toString().padStart(2, '0')}:00`;
						};

						return `
							<tr class="session-row ${isActive ? 'active' : ''}">
								<td style="font-weight: bold;">${s.name}</td>
								<td>${formatTime(s.utcStart, localOffset)} - ${formatTime(s.utcEnd, localOffset)}</td>
								<td>${s.utcStart.toString().padStart(2, '0')}:00 - ${s.utcEnd.toString().padStart(2, '0')}:00</td>
								<td>${formatTime(s.utcStart, brokerOffset)} - ${formatTime(s.utcEnd, brokerOffset)}</td>
								<td>
									<span class="session-status"></span>
									${isActive ? 'Active' : 'Closed'}
								</td>
							</tr>
						`;
					}).join('')}
				</tbody>
			</table>
		`;

		container.innerHTML = html;
	}

	static renderHeatmap(hourData: any[]) {
		const container = d.qs('#session-heatmap');
		if (!container) return;
		container.innerHTML = '';

		// Sort by hour 0-23
		const sorted = [...hourData].sort((a, b) => a.hour - b.hour);
		
		// Find max profit and max loss for scaling colors
		const maxProfit = Math.max(...sorted.map(h => h.pnl), 1);
		const maxLoss = Math.min(...sorted.map(h => h.pnl), -1);

		sorted.forEach(h => {
			const hourDiv = document.createElement('div');
			hourDiv.className = 'heatmap-hour';
			
			// Calculate color intensity
			let bg = 'var(--theme-bg-input)';
			if (h.pnl > 0) {
				const intensity = Math.min(0.8, (h.pnl / maxProfit) * 0.8 + 0.1);
				bg = `rgba(16, 185, 129, ${intensity})`;
			} else if (h.pnl < 0) {
				const intensity = Math.min(0.8, (Math.abs(h.pnl) / Math.abs(maxLoss)) * 0.8 + 0.1);
				bg = `rgba(239, 68, 68, ${intensity})`;
			}

			hourDiv.style.background = bg;
			hourDiv.title = `Hour ${h.hour}:00
PnL: $${h.pnl.toFixed(2)}
Trades: ${h.trades}`;

			hourDiv.innerHTML = `
				<span class="hour-label">${h.hour.toString().padStart(2, '0')}</span>
				<span class="hour-val">${h.trades}tr</span>
			`;

			container.appendChild(hourDiv);
		});
	}

	static renderVolumeChart(hourData: any[]) {
		const container = d.qs('#volume-chart-container');
		if (!container) return;
		container.innerHTML = '';

		const sorted = [...hourData].sort((a, b) => a.hour - b.hour);
		const maxTrades = Math.max(...sorted.map(h => h.trades), 1);

		sorted.forEach(h => {
			const bar = document.createElement('div');
			bar.className = 'volume-bar';
			const heightPct = (h.trades / maxTrades) * 100;
			bar.style.height = `${Math.max(5, heightPct)}%`;
			bar.title = `${h.hour}:00 - ${h.trades} Trades`;
			container.appendChild(bar);
		});
	}

	static renderInsights(hourData: any[]) {
		const container = d.qs('#analytics-insights');
		if (!container) return;
		
		const bestHour = [...hourData].sort((a, b) => b.pnl - a.pnl)[0];
		const worstHour = [...hourData].sort((a, b) => a.pnl - b.pnl)[0];
		const mostActive = [...hourData].sort((a, b) => b.trades - a.trades)[0];

		container.innerHTML = `
			<div class="insight-item">
				<div style="font-weight: bold; color: var(--theme-status-profit);">Golden Hour: ${bestHour.hour}:00</div>
				<p style="font-size: 0.85rem; color: var(--theme-text-secondary);">Your most profitable time to trade. Net PnL: $${bestHour.pnl.toFixed(2)}</p>
			</div>
			<div class="insight-item" style="margin-top: 10px;">
				<div style="font-weight: bold; color: var(--theme-status-loss);">Danger Zone: ${worstHour.hour}:00</div>
				<p style="font-size: 0.85rem; color: var(--theme-text-secondary);">Historically your worst hour. Exercise extreme caution or avoid this window.</p>
			</div>
			<div class="insight-item" style="margin-top: 10px;">
				<div style="font-weight: bold; color: var(--theme-accent-primary);">Peak Activity: ${mostActive.hour}:00</div>
				<p style="font-size: 0.85rem; color: var(--theme-text-secondary);">You take the most trades during this window (${mostActive.trades} trades).</p>
			</div>
		`;
	}
}
